/**
 * WallSnobs PDP — shared selection state
 *
 * The page is one continuous design but is built from independent sections, so
 * they cannot read each other's Liquid. This is the single place that knows what
 * the customer has selected.
 *
 * Sections render EVERY state server-side and toggle visibility in response to
 * these events. Nothing is fetched on selection, so switching colorway is
 * instant and the page is complete before JavaScript runs.
 *
 * Sections subscribe with:
 *   WSPdp.subscribe(function (state, changed) { ... })
 *
 * and change state with:
 *   WSPdp.set({ colorway: 'Moss' })
 *
 * `changed` is an array of the keys that actually differed, so a section that
 * only cares about material can ignore a colorway switch.
 */
window.WSPdp = (function () {
  'use strict';

  var listeners = [];
  var variants = [];
  var state = { colorway: null, material: null, photo: 0 };

  /** Mirrors Liquid's | handleize so JS and Liquid agree on element ids. */
  function handleize(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function emit(changed) {
    for (var i = 0; i < listeners.length; i++) {
      try {
        listeners[i](state, changed);
      } catch (err) {
        // One bad subscriber must not stop the rest of the page updating.
        console.error('[ws-pdp] subscriber failed', err);
      }
    }
  }

  /**
   * Variant data is injected by the buy section. Until then, selection still
   * works — sections just can't resolve a variant id yet.
   */
  function registerVariants(list) {
    variants = Array.isArray(list) ? list : [];
  }

  /** The purchasable (non-sample) variant for a selection. */
  function unitVariant(colorway, material) {
    return variants.find(function (v) {
      return v.colorway === (colorway || state.colorway) &&
             v.material === (material || state.material) &&
             v.type !== 'Sample';
    }) || null;
  }

  /** The sample variant for a selection. */
  function sampleVariant(colorway, material) {
    return variants.find(function (v) {
      return v.colorway === (colorway || state.colorway) &&
             v.material === (material || state.material) &&
             v.type === 'Sample';
    }) || null;
  }

  /**
   * Keep ?variant= honest so refreshes, shares and back-button all land on the
   * same selection. replaceState, not pushState — a colorway switch is not a
   * separate page in the customer's mind, and filling their history with them
   * makes Back feel broken.
   */
  function syncUrl() {
    var variant = unitVariant();
    if (!variant || !window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    if (String(url.searchParams.get('variant')) === String(variant.id)) return;
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  function set(patch) {
    var changed = [];
    Object.keys(patch || {}).forEach(function (key) {
      if (!(key in state)) return;
      if (state[key] === patch[key]) return;
      state[key] = patch[key];
      changed.push(key);
    });
    if (!changed.length) return;

    // A colorway's photo set is its own — carrying an index across is how you
    // land on photo 4 of a colorway that only has one.
    if (changed.indexOf('colorway') !== -1 && changed.indexOf('photo') === -1) {
      state.photo = 0;
      changed.push('photo');
    }

    emit(changed);
    syncUrl();
  }

  function init(initial) {
    Object.keys(initial || {}).forEach(function (key) {
      if (key in state && initial[key] != null) state[key] = initial[key];
    });
    // Announce the starting state so sections can set themselves up through the
    // same path they use for every later change — one code path, not two.
    emit(['colorway', 'material', 'photo']);
  }

  return {
    get state() {
      return Object.assign({}, state);
    },
    set: set,
    init: init,
    registerVariants: registerVariants,
    unitVariant: unitVariant,
    sampleVariant: sampleVariant,
    handleize: handleize,
    subscribe: function (fn) {
      if (typeof fn !== 'function') return function () {};
      listeners.push(fn);
      // Late subscribers get the current state immediately rather than waiting
      // for the next change, so section load order doesn't matter.
      if (state.colorway || state.material) fn(state, ['colorway', 'material', 'photo']);
      return function unsubscribe() {
        var i = listeners.indexOf(fn);
        if (i !== -1) listeners.splice(i, 1);
      };
    }
  };
})();
